import React, { useCallback, useEffect, useState } from "react";
import { MdGroups, MdPersonRemove } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import Badge from "components/badge/Badge";
import { IconButton } from "components/button";
import { useConfirm } from "components/dialog/ConfirmProvider";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { palette } from "lib/chartTheme";
import notify from "lib/notify";

const Groups = ({ userId }) => {
  const confirm = useConfirm();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const fetchUserGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AtriumApi.get(`/v1/groups/user/${userId}`);
      setGroups(response.data.success ? response.data.data || [] : []);
    } catch (err) {
      setGroups([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const handleRemoveFromGroup = async (group) => {
    const confirmed = await confirm({
      title: `Remove from "${group.name}"?`,
      message: "This person will no longer be a member of this group.",
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      setRemovingId(group.id);
      await AtriumApi.delete(`/v1/groups/${group.id}/members/${userId}`);
      notify.success(`Removed from ${group.name}`);
      await fetchUserGroups();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not remove from group"));
    } finally {
      setRemovingId(null);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserGroups();
    }
  }, [userId, fetchUserGroups]);

  if (loading) {
    return (
      <div aria-busy="true" className="flex flex-col gap-4 py-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton lines={2} className="flex-1" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={getApiErrorMessage(error, "Groups could not be loaded.")}
        onRetry={fetchUserGroups}
      />
    );
  }

  if (groups.length === 0) {
    return <EmptyState variant="inline" title="Not a member of any groups" />;
  }

  return (
    <ul className="divide-y divide-line">
      {groups.map((group) => (
        <li key={group.id} className="flex items-center justify-between gap-3 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: group.color || palette.sage }}
            >
              <MdGroups aria-hidden="true" className="h-5 w-5 text-white" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-fg">{group.name}</p>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-fg-muted">
                <span>{group.memberCount || 0} members</span>
                {group.userRole && group.userRole !== "member" && (
                  <Badge tone="neutral" size="sm" className="capitalize">
                    {group.userRole}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <IconButton
            icon={<MdPersonRemove className="h-5 w-5" />}
            label={`Remove from ${group.name}`}
            size="sm"
            loading={removingId === group.id}
            onClick={() => handleRemoveFromGroup(group)}
          />
        </li>
      ))}
    </ul>
  );
};

export default Groups;
